import { gql } from "@apollo/client";

// ============================================================
// CLIENTS MODULE - GraphQL Queries & Mutations
// ============================================================
// Single source of truth for all GraphQL operations used
// across the clients page module components.
// ============================================================

// ----------------------------------------------------------
// QUERIES - Client Data
// ----------------------------------------------------------

/** Fetch all clients (used by ClientsList, Permissions) */
export const GET_CLIENTS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      username
      email
      phoneNumber
      permissions
      tags
      createdAt
      lastLoginAt
    }
  }
`;

/** Fetch all clients with extra fields for Permissions page */
export const GET_CLIENTS_WITH_CREATED_AT = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      phoneNumber
      permissions
      createdAt
    }
  }
`;

/** Fetch a single client by ID (used by ClientDetails) */
export const GET_CLIENT = gql`
  query GetClient($id: ID!) {
    client(id: $id) {
      id
      fName
      lName
      email
      phoneNumber
      createdAt
      updatedAt
    }
  }
`;

/** Fetch websites owned by a client (used by ClientDetails) */
export const GET_WEBSITES_BY_CLIENT = gql`
  query GetWebsitesByClient($clientId: ID!) {
    websitesByClient(clientId: $clientId) {
      id
      name
      domain
      websiteUrl
      clientId
      createdAt
      updatedAt
    }
  }
`;

/** Fetch products by a client (used by ClientDetails) */
export const GET_PRODUCTS_BY_CLIENT = gql`
  query GetProductsByClient($clientId: ID!) {
    productsByClient(clientId: $clientId) {
      id
      name
      description
      price
      status
      createdAt
    }
  }
`;

/** Fetch the ClientPermission enum values from the GraphQL schema */
export const GET_PERMISSION_ENUM = gql`
  query GetPermissionEnum {
    __type(name: "ClientPermission") {
      enumValues {
        name
        description
      }
    }
  }
`;

/** Fetch all unique client tags for the tenant */
export const GET_ALL_TAGS = gql`
  query GetAllClientTags {
    allClientTags
  }
`;

/** Find potential duplicate clients (same email, phone, or similar names) */
export const FIND_POTENTIAL_DUPLICATE_CLIENTS = gql`
  query FindPotentialDuplicateClients {
    findPotentialDuplicateClients {
      groupId
      reason
      matchValue
      count
      clients {
        id
        fName
        lName
        email
        phoneNumber
        createdAt
        tags
      }
    }
  }
`;

/** Search clients with optional text and tag filters */
export const SEARCH_CLIENTS = gql`
  query SearchClients($search: String, $tags: [String!]) {
    searchClients(search: $search, tags: $tags) {
      id
      fName
      lName
      email
      phoneNumber
      tags
      permissions
    }
  }
`;

/**
 * Dev-mode Shift+hover reveal: "who on this tenant can reach this page?"
 * Backend uses `{ permissions: { $in: permissions } }` — matches any client
 * whose permissions array contains at least one of the supplied strings.
 * See `vault/how-to/dev-mode-permission-hover.md`.
 */
export const CLIENTS_BY_PERMISSIONS = gql`
  query ClientsByPermissions($permissions: [String!]!, $limit: Int) {
    clientsByPermissions(permissions: $permissions, limit: $limit) {
      id
      fName
      lName
      email
    }
  }
`;

/** Search clients for the merge page (includes extra fields) */
export const SEARCH_CLIENTS_FOR_MERGE = gql`
  query SearchClientsForMerge($search: String) {
    searchClients(search: $search) {
      id
      fName
      lName
      email
      phoneNumber
      username
      tags
      permissions
      createdAt
      mergedIntoClientId
    }
  }
`;

/** Fetch all clients for merge browse mode */
export const GET_ALL_CLIENTS_FOR_MERGE = gql`
  query GetAllClientsForMerge {
    clients {
      id
      fName
      lName
      email
      phoneNumber
      username
      tags
      permissions
      createdAt
      mergedIntoClientId
    }
  }
`;

/**
 * Employee aliases for the Permissions "Clone From" search.
 * Lets the picker find a client by their linked employee's nickname / work email /
 * employee number (identity fName/lName/email still come from the Client record).
 * Loaded with errorPolicy:'all' + skip so tenants without the employees module —
 * or callers lacking employee-view permission — silently degrade to client-only search.
 */
export const GET_EMPLOYEE_ALIASES_FOR_CLONE = gql`
  query GetEmployeeAliasesForClone {
    employees {
      id
      preferredName
      workEmail
      employeeNumber
      client {
        id
      }
    }
  }
`;

/** Lightweight employee lookup to show which clients have employee records */
export const GET_EMPLOYEE_CLIENT_LOOKUP = gql`
  query GetEmployeeClientLookup {
    employeesPaginated(input: { offset: 0, limit: 9999 }) {
      employees {
        id
        client {
          id
        }
      }
    }
  }
`;

// ----------------------------------------------------------
// MUTATIONS - Client Management
// ----------------------------------------------------------

/** Create a new client record */
export const CREATE_CLIENT = gql`
  mutation CreateClient($input: ClientInput!) {
    createClient(input: $input) {
      id
      fName
      lName
      email
      phoneNumber
      createdAt
      updatedAt
    }
  }
`;

/** Update an existing client record */
export const UPDATE_CLIENT = gql`
  mutation UpdateClient($id: ID!, $input: UpdateClientInput!) {
    updateClient(id: $id, input: $input) {
      id
      fName
      lName
      email
      phoneNumber
      tags
    }
  }
`;

/** Delete a client record */
export const DELETE_CLIENT = gql`
  mutation DeleteClient($id: ID!) {
    deleteClient(id: $id)
  }
`;

// ----------------------------------------------------------
// MUTATIONS - Permission Management
// ----------------------------------------------------------

/** Add a permission to a client */
export const ADD_CLIENT_PERMISSION = gql`
  mutation AddClientPermission($clientId: ID!, $permission: ClientPermission!) {
    addClientPermission(clientId: $clientId, permission: $permission) {
      id
      permissions
    }
  }
`;

/** Remove a permission from a client */
export const REMOVE_CLIENT_PERMISSION = gql`
  mutation RemoveClientPermission($clientId: ID!, $permission: ClientPermission!) {
    removeClientPermission(clientId: $clientId, permission: $permission) {
      id
      permissions
    }
  }
`;

/** Set all permissions on a client (used by clone permissions feature) */
export const SET_CLIENT_PERMISSIONS = gql`
  mutation SetClientPermissions($clientId: ID!, $permissions: [ClientPermission!]!) {
    setClientPermissions(clientId: $clientId, permissions: $permissions) {
      id
      permissions
    }
  }
`;

// ----------------------------------------------------------
// MUTATIONS - Merge Clients
// ----------------------------------------------------------

/** Merge duplicate clients (legacy - used in ClientsList inline modal) */
export const MERGE_CLIENTS = gql`
  mutation MergeClients($keepClientId: ID!, $mergeClientIds: [ID!]!) {
    mergeClients(keepClientId: $keepClientId, mergeClientIds: $mergeClientIds) {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

/** Comprehensive merge with credential/record transfer (used by MergeClients page) */
export const MERGE_CLIENT_ACCOUNTS = gql`
  mutation MergeClientAccounts($keepClientId: ID!, $mergeClientIds: [ID!]!) {
    mergeClientAccounts(keepClientId: $keepClientId, mergeClientIds: $mergeClientIds) {
      id
      fName
      lName
      email
      phoneNumber
      username
      tags
      permissions
    }
  }
`;

// ==========================================
// CLIENT NOTIFICATION RULES
// ==========================================

export const GET_CLIENT_NOTIFICATION_RULES = gql`
  query GetClientNotificationRules {
    clientNotificationRules {
      id
      name
      trigger
      recipientType
      recipientIds
      recipientTag
      channels
      timing
      timingMinutes
      isEnabled
      messageTemplate
      createdAt
    }
  }
`;

export const CREATE_CLIENT_NOTIFICATION_RULE = gql`
  mutation CreateClientNotificationRule($input: CreateClientNotificationRuleInput!) {
    createClientNotificationRule(input: $input) {
      id
      name
      trigger
      recipientType
      channels
      isEnabled
    }
  }
`;

export const UPDATE_CLIENT_NOTIFICATION_RULE = gql`
  mutation UpdateClientNotificationRule($input: UpdateClientNotificationRuleInput!) {
    updateClientNotificationRule(input: $input) {
      id
      name
      trigger
      recipientType
      recipientIds
      recipientTag
      channels
      timing
      timingMinutes
      isEnabled
      messageTemplate
    }
  }
`;

export const DELETE_CLIENT_NOTIFICATION_RULE = gql`
  mutation DeleteClientNotificationRule($id: ID!) {
    deleteClientNotificationRule(id: $id)
  }
`;

export const TOGGLE_CLIENT_NOTIFICATION_RULE = gql`
  mutation ToggleClientNotificationRule($id: ID!) {
    toggleClientNotificationRule(id: $id) {
      id
      isEnabled
    }
  }
`;

// Shared — used by the NotificationSettings recipient picker
export const GET_EMPLOYEES_FOR_CLIENT_NOTIFICATIONS = gql`
  query GetEmployeesForClientNotifications {
    employees {
      id
      workEmail
      client {
        id
        fName
        lName
      }
    }
  }
`;

export const GET_CLIENTS_FOR_CLIENT_NOTIFICATIONS = gql`
  query GetClientsForClientNotifications {
    clients {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

/** Self-service: save the logged-in user's home-page quick-links curation
 *  (module launcher order + hidden set). Auth-only on the backend — scoped to
 *  the caller's own Client record; returns the updated preferences.quickLinks. */
export const SAVE_MY_QUICKLINKS = gql`
  mutation SaveMyQuickLinks($order: [String!]!, $hidden: [String!]!) {
    saveMyQuickLinks(order: $order, hidden: $hidden) {
      quickLinks {
        order
        hidden
      }
    }
  }
`;
